/**
 * The in-app book API contract (ebook-plan § 3.6, block tree § 3.5): TypeScript types and the
 * zod schemas that check every response at the boundary. One module, so the UI, the API client,
 * the e2e mocks and the unit fixtures all build on the same shapes.
 *
 * - `GET /api/book` → `BookState`: `{visibility: "hidden"}` when the book is off or not for this
 *   account, else the visible state (access, edition, TOC, progress, purchase, site order).
 * - `GET /api/book/chapters/{id}` → `BookChapter`: one chapter as a typed block tree. The tree is
 *   rendered as React elements (no HTML strings), so the schema is the allowlist: an unknown node
 *   type, a link outside https://lukaaji.com or an asset URL that is not ours fails the payload.
 * - `PUT /api/book/progress` ← `ProgressIn` (Readium-Locator shape, last write wins).
 * - Phase B: `POST /consent` → `{consentId}`, `POST /checkout` → `{url}`, `POST /checkout/verify` →
 *   `{owned, status}`, `POST /redeem` and `POST /claim` → `{owned}`, `GET /files` → `{pdf?, epub?}`.
 * - Errors: `403 book_locked`, `404` (hidden), `429 book_rate_limited`, and the purchase codes of
 *   phase B (`BOOK_ERROR_CODES`), as `{detail: "<code>"}` (or `{detail: {code}}`).
 *
 * The server omits optional fields that are null (`response_model_exclude_none`), so every
 * nullable field here is `.nullable().default(null)`: absent and null parse to the same null.
 * Pinned against the real router by src/test/contract/book-api.json
 * (backend/tests/book/test_contract_snapshot.py) in bookContract.snapshot.test.ts.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------------------------
// Block tree (§ 3.5)
// ---------------------------------------------------------------------------------------------

/** Nesting limit for container nodes (text and br are leaves and do not count). */
export const MAX_BLOCK_DEPTH = 8;

export interface TextNode { t: 'text'; v: string }
export interface BreakNode { t: 'br' }
export interface EmphasisNode { t: 'em' | 'strong'; c: Inline[] }
export interface SpanNode { t: 'span'; cls?: string; deco?: boolean; c: Inline[] }
/** A link to another place in the book: a chapter id and, optionally, a block in it. */
export interface InternalLinkNode { t: 'link'; to: { chapter: string; block?: string | null }; c?: Inline[] }
/** A link out of the book: only https://lukaaji.com (§ 3.5). */
export interface ExternalLinkNode { t: 'ext'; href: string; c: Inline[] }
/** A footnote reference; `note` is the id of the `note` block. */
export interface NoteRefNode { t: 'noteref'; note: string; c?: Inline[] }

export type Inline = TextNode | BreakNode | EmphasisNode | SpanNode | InternalLinkNode | ExternalLinkNode | NoteRefNode;

interface BlockBase {
  id: string;
  cls?: string;
  /** The source element had aria-hidden="true": decoration, kept out of copy and search. */
  deco?: boolean;
}

export interface HeadingBlock extends BlockBase { t: 'h'; l: 1 | 2 | 3 | 4 | 5; c: Inline[] }
export interface ParagraphBlock extends BlockBase { t: 'p'; c: Inline[] }
export interface ListItem { id: string; c: Inline[] }
export interface ListBlock extends BlockBase { t: 'ul' | 'ol'; items: ListItem[] }
export interface QuoteBlock extends BlockBase { t: 'quote'; c: Block[] }
/** A `div` with an allowlisted class, or a `section` (its `epub:type` as `role`). */
export interface BoxBlock extends BlockBase { t: 'box'; role?: string; c: Block[] }
export interface FigureBlock extends BlockBase { t: 'fig'; asset: string; cap?: Inline[] }
export interface TableCell { h: boolean; scope?: 'col' | 'row'; cs?: number; rs?: number; c: Inline[] }
export interface TableBlock extends BlockBase { t: 'table'; rows: { cells: TableCell[] }[] }
/** A footnote (`epub:type="footnote"`). */
export interface NoteBlock extends BlockBase { t: 'note'; c: (Block | Inline)[] }

export type Block = HeadingBlock | ParagraphBlock | ListBlock | QuoteBlock | BoxBlock | FigureBlock | TableBlock | NoteBlock;

const idSchema = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);
/** Space-separated class names as the ingest writes them; the renderer emits only allowlisted ones. */
const clsSchema = z.string().regex(/^[a-z][a-z0-9-]{0,39}(?: [a-z][a-z0-9-]{0,39}){0,4}$/);
const roleSchema = z.string().regex(/^[a-z][a-z-]{0,31}$/);

/**
 * The § 3.5 rule for `ext` links: scheme https, host exactly lukaaji.com, no userinfo, no port,
 * and an empty or `/…` path (no query, no fragment). The ingest applies the same rule; v6 has
 * exactly `https://lukaaji.com` (no slash) three times.
 */
export function isAllowedExternalHref(href: string): boolean {
  if (!/^https:\/\/lukaaji\.com(?:\/[A-Za-z0-9\-._~/%]*)?$/.test(href)) return false;
  try {
    const url = new URL(href);
    return url.protocol === 'https:' && url.hostname === 'lukaaji.com' && url.port === '' && url.username === '' && url.password === '';
  } catch {
    return false;
  }
}

const inlineSchema: z.ZodType<Inline> = z.lazy(() =>
  z.discriminatedUnion('t', [
    z.object({ t: z.literal('text'), v: z.string() }),
    z.object({ t: z.literal('br') }),
    z.object({ t: z.enum(['em', 'strong']), c: z.array(inlineSchema) }),
    z.object({ t: z.literal('span'), cls: clsSchema.optional(), deco: z.boolean().optional(), c: z.array(inlineSchema) }),
    z.object({
      t: z.literal('link'),
      to: z.object({ chapter: idSchema, block: idSchema.nullable().optional() }),
      c: z.array(inlineSchema).optional(),
    }),
    z.object({ t: z.literal('ext'), href: z.string().refine(isAllowedExternalHref, 'external link outside lukaaji.com'), c: z.array(inlineSchema) }),
    z.object({ t: z.literal('noteref'), note: idSchema, c: z.array(inlineSchema).optional() }),
  ]),
);

const blockBase = { id: idSchema, cls: clsSchema.optional(), deco: z.boolean().optional() };

const blockSchema: z.ZodType<Block> = z.lazy(() =>
  z.discriminatedUnion('t', [
    z.object({ ...blockBase, t: z.literal('h'), l: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]), c: z.array(inlineSchema) }),
    z.object({ ...blockBase, t: z.literal('p'), c: z.array(inlineSchema) }),
    z.object({ ...blockBase, t: z.enum(['ul', 'ol']), items: z.array(z.object({ id: idSchema, c: z.array(inlineSchema) })) }),
    z.object({ ...blockBase, t: z.literal('quote'), c: z.array(blockSchema) }),
    z.object({ ...blockBase, t: z.literal('box'), role: roleSchema.optional(), c: z.array(blockSchema) }),
    z.object({ ...blockBase, t: z.literal('fig'), asset: idSchema, cap: z.array(inlineSchema).optional() }),
    z.object({
      ...blockBase,
      t: z.literal('table'),
      rows: z.array(
        z.object({
          cells: z.array(
            z.object({
              h: z.boolean(),
              scope: z.enum(['col', 'row']).optional(),
              cs: z.number().int().min(1).max(50).optional(),
              rs: z.number().int().min(1).max(50).optional(),
              c: z.array(inlineSchema),
            }),
          ),
        }),
      ),
    }),
    z.object({ ...blockBase, t: z.literal('note'), c: z.array(z.union([blockSchema, inlineSchema])) }),
  ]),
);

/**
 * Nesting depth of a raw (unchecked) tree: objects with a `t` other than the leaves text and br
 * count one level. Iterative, so a hostile payload cannot overflow the stack before zod sees it.
 */
export function treeDepth(value: unknown): number {
  let max = 0;
  const stack: { node: unknown; depth: number }[] = [{ node: value, depth: 0 }];
  while (stack.length > 0) {
    const { node, depth } = stack.pop() as { node: unknown; depth: number };
    if (Array.isArray(node)) {
      for (const child of node) stack.push({ node: child, depth });
    } else if (node !== null && typeof node === 'object') {
      const record = node as Record<string, unknown>;
      const counts = typeof record.t === 'string' && record.t !== 'text' && record.t !== 'br';
      const next = counts ? depth + 1 : depth;
      if (next > max) max = next;
      if (max > MAX_BLOCK_DEPTH) return max;
      for (const child of Object.values(record)) {
        if (child !== null && typeof child === 'object') stack.push({ node: child, depth: next });
      }
    }
  }
  return max;
}

const blocksSchema = z
  .unknown()
  .refine((value) => treeDepth(value) <= MAX_BLOCK_DEPTH, `block tree deeper than ${MAX_BLOCK_DEPTH}`)
  .pipe(z.array(blockSchema));

// ---------------------------------------------------------------------------------------------
// GET /api/book
// ---------------------------------------------------------------------------------------------

const fractionSchema = z.number().min(0).max(1);

export const BOOK_ACCESS = ['none', 'preview', 'full'] as const;
export type BookAccess = (typeof BOOK_ACCESS)[number];
export const BOOK_SOURCES = ['stripe', 'prestopay', 'grant', 'plan_perk'] as const;
export type BookSource = (typeof BOOK_SOURCES)[number];

/** A field the server sends only when it is not null. */
const optionalNull = <T extends z.ZodType>(schema: T) => schema.nullable().default(null);

export const tocEntrySchema = z.object({
  id: idSchema,
  /** Stable spine key (`ch05`), the same across editions. */
  key: z.string().min(1).max(32),
  title: z.string(),
  /** The part's title for chapters inside a part, else null. */
  part: optionalNull(z.string()),
  /** The EPUB's TOC level; null for a spine item its TOC does not list (cover, title page, …). */
  level: optionalNull(z.number().int().min(1).max(6)),
  preview: z.boolean(),
  words: z.number().int().nonnegative(),
});
export type TocEntry = z.infer<typeof tocEntrySchema>;

/** Saved reading position (Readium-Locator shape). */
export const locatorSchema = z.object({
  chapterId: idSchema,
  blockId: idSchema.nullable(),
  progression: fractionSchema,
  totalProgression: fractionSchema,
  updatedAt: z.string().optional(),
});
export type Locator = z.infer<typeof locatorSchema>;

const signedFileSchema = z.object({
  /** Signed for 5 minutes (`lukaaji/book-file/v1`), same origin. */
  url: z.string().regex(/^\/api\/book\/download\/(?:pdf|epub)\?[A-Za-z0-9\-._~%&=]*$/),
  size: z.number().int().nonnegative(),
});

/** The trader block shown before the order (ZZP čl. 61(3), legal L6, critic CR-7); proper nouns. */
export const sellerSchema = z.object({ name: z.string().min(1), address: z.string().min(1), phone: z.string().min(1), email: z.string().min(1) });
export type BookSeller = z.infer<typeof sellerSchema>;

/** Why the purchase is not offered (backend services/purchase.py `purchase_availability`). */
export const PURCHASE_REASONS = ['owned', 'off', 'staff_only', 'price_unset', 'seller_unset', 'claimable'] as const;
export type PurchaseReason = (typeof PURCHASE_REASONS)[number];

const hiddenStateSchema = z.object({ visibility: z.literal('hidden') });

const visibleStateSchema = z.object({
  visibility: z.literal('visible'),
  access: z.enum(BOOK_ACCESS),
  owned: z.boolean(),
  source: optionalNull(z.enum(BOOK_SOURCES)),
  /** Null until an edition is ingested (production stays empty until Luka's written OK, D22). */
  edition: z
    .object({
      version: z.string(),
      title: z.string(),
      author: z.string(),
      lang: z.string(),
      totalWords: z.number().int().nonnegative(),
    })
    .nullable(),
  toc: z.array(tocEntrySchema),
  progress: locatorSchema.nullable().optional(),
  purchase: z.object({
    /** True only when this account may buy now (purchase mode, price, seller, not owned, nothing to claim). */
    available: z.boolean(),
    priceCents: z.number().int().positive(),
    currency: z.literal('EUR'),
    /** Set only while `available`. */
    consentVersion: optionalNull(z.string().min(1)),
    /** Unknown future reasons parse too; the UI treats any reason as "not offered". */
    reason: optionalNull(z.string()),
    /** Set only while `available` (the server refuses the purchase without a complete seller). */
    seller: optionalNull(sellerSchema),
  }),
  siteOrder: z.object({ claimable: z.boolean(), needsVerifiedEmail: z.boolean() }),
  /** Owner with downloadable files on the server: links come from `GET /api/book/files`. */
  downloads: z.boolean().default(false),
});

export const bookStateSchema = z.discriminatedUnion('visibility', [hiddenStateSchema, visibleStateSchema]);
export type BookState = z.infer<typeof bookStateSchema>;
export type BookVisibleState = z.infer<typeof visibleStateSchema>;

// ---------------------------------------------------------------------------------------------
// GET /api/book/chapters/{id}
// ---------------------------------------------------------------------------------------------

export const bookAssetSchema = z.object({
  /** Signed, same-origin URL (`lukaaji/book-asset/v1`). */
  url: z.string().regex(/^\/api\/book\/assets\/[A-Za-z0-9_-]{1,64}(?:\?[A-Za-z0-9\-._~%&=]*)?$/),
  w: z.number().int().positive(),
  h: z.number().int().positive(),
  alt: z.string(),
  longDescription: z.string().nullable().optional(),
});
export type BookAsset = z.infer<typeof bookAssetSchema>;

export const bookChapterSchema = z.object({
  id: idSchema,
  key: z.string().min(1).max(32),
  title: z.string(),
  part: optionalNull(z.string()),
  order: z.number().int().nonnegative(),
  prev: optionalNull(idSchema),
  next: optionalNull(idSchema),
  words: z.number().int().nonnegative(),
  wordsBefore: z.number().int().nonnegative(),
  totalWords: z.number().int().nonnegative(),
  /** The book's language (`cnr`), for `lang` on the book text. */
  lang: z.string().min(1),
  blocks: blocksSchema,
  assets: z.record(idSchema, bookAssetSchema),
  /** The reader's own email and opaque copy code, shown at chapter start and end (§ 3.7). */
  watermark: z.object({ email: z.string(), code: z.string() }),
});
export type BookChapter = z.infer<typeof bookChapterSchema>;

// ---------------------------------------------------------------------------------------------
// PUT /api/book/progress
// ---------------------------------------------------------------------------------------------

export const progressInSchema = z.object({
  chapterId: idSchema,
  blockId: idSchema,
  progression: fractionSchema,
  totalProgression: fractionSchema,
  /** ISO time of the reading position on the device; the server rejects older writes. */
  clientUpdatedAt: z.string().min(1),
});
export type ProgressIn = z.infer<typeof progressInSchema>;

/** `saved: false` when the server already holds a newer position (it returns that one). */
export const progressOutSchema = z.object({ saved: z.boolean(), progress: optionalNull(locatorSchema) });
export type ProgressOut = z.infer<typeof progressOutSchema>;

// ---------------------------------------------------------------------------------------------
// Phase B: purchase, site buyers, downloads
// ---------------------------------------------------------------------------------------------

/** `POST /api/book/consent` body: the SHA-256 is of the exact consent string rendered (CR-5). */
export const consentInSchema = z.object({
  consentVersion: z.string().min(1).max(40),
  language: z.string().regex(/^[a-z]{2}$/),
  textSha256: z.string().regex(/^[0-9a-f]{64}$/),
});
export type ConsentIn = z.infer<typeof consentInSchema>;

export const consentOutSchema = z.object({ consentId: z.string().min(1).max(36) });

/** Stripe Checkout lives on https only; anything else is refused before `location.assign`. */
export const checkoutOutSchema = z.object({ url: z.string().url().startsWith('https://') });

export const verifyOutSchema = z.object({
  owned: z.boolean(),
  /** processing: not paid (or not fulfilled) yet; refunded: paid and already refunded. */
  status: z.enum(['owned', 'processing', 'refunded']),
});
export type VerifyOut = z.infer<typeof verifyOutSchema>;

export const ownedOutSchema = z.object({ owned: z.boolean() });

export const bookFilesSchema = z.object({ pdf: signedFileSchema.optional(), epub: signedFileSchema.optional() });
export type BookFiles = z.infer<typeof bookFilesSchema>;

// ---------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------

export const BOOK_ERROR_CODES = [
  'book_locked',
  'book_owned',
  'book_claimable',
  'consent_stale',
  'book_rate_limited',
  'book_purchase_unavailable',
  'book_checkout_failed',
  'invalid_code',
  'nothing_to_claim',
] as const;
export type BookErrorCode = (typeof BOOK_ERROR_CODES)[number];

const rejectionSchema = z.object({
  status: z.number().optional(),
  data: z.object({ detail: z.union([z.string(), z.object({ code: z.string() })]) }).optional().catch(undefined),
});

function isBookErrorCode(value: string): value is BookErrorCode {
  return (BOOK_ERROR_CODES as readonly string[]).includes(value);
}

/** The § 3.6 code of an apiClient rejection (`{status, data}`), or null. A bare 429 is the rate limit. */
export function bookErrorCode(error: unknown): BookErrorCode | null {
  const parsed = rejectionSchema.safeParse(error);
  if (!parsed.success) return null;
  const detail = parsed.data.data?.detail;
  const code = typeof detail === 'string' ? detail : detail?.code;
  if (code && isBookErrorCode(code)) return code;
  return parsed.data.status === 429 ? 'book_rate_limited' : null;
}
